<!-- src/components/auth/UnauthorizedState.vue -->
<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })
const props = withDefaults(
  defineProps<{
    /** Nome da permissão para sugerir na mensagem */
    permission?: string
    /** Título e descrição customizáveis */
    title?: string
    description?: string
    /** Mostra botão “Voltar” */
    showBack?: boolean
    /** Label do CTA opcional (ex.: “Pedir acesso”) */
    ctaLabel?: string
    /** Tamanho do ícone (px) */
    sizePx?: number
    /** Layout mais compacto */
    dense?: boolean
  }>(),
  {
    permission: undefined,
    title: undefined,
    description: undefined,
    ctaLabel: undefined,
    showBack: true,
    sizePx: 160,
    dense: false,
  }
)

defineEmits<{
  (e: 'cta'): void
}>()

const defaultDescription = computed(() => {
  return props.permission
    ? t('permissions.permissionMessage') // quando há uma permissão específica
    : t('permissions.permissionMessageGeneric') // fallback genérico
})
</script>
<template>
  <div
    class="unauth-wrapper d-flex align-center justify-center"
    :class="dense ? 'pa-8' : 'pa-12'"
    role="region"
    aria-label="Acesso não autorizado"
    style="margin-top: 10rem"
  >
    <div class="text-center">
      <!-- Lupa SVG -->
      <div
        class="mx-auto mb-8"
        :style="{ width: sizePx + 'px', height: sizePx + 'px' }"
        aria-hidden="true"
      >
        <svg viewBox="0 0 512 512" class="unauth-svg">
          <!-- círculo -->
          <circle cx="232" cy="232" r="160" class="ring" />
          <!-- haste -->
          <rect
            x="318"
            y="318"
            width="176"
            height="28"
            rx="14"
            transform="rotate(45 318 318)"
            class="handle"
          />
          <!-- bolinha de fundo do ? -->
          <circle cx="232" cy="232" r="56" class="dot-bg" />
          <!-- ? -->
          <text x="232" y="246" text-anchor="middle" class="question">?</text>
        </svg>
      </div>

      <h2 class="text-h5 font-weight-medium mb-2">
        {{ title ?? 'Acesso não autorizado' }}
      </h2>

      <p class="text-body-2 text-medium-emphasis mb-6">
        {{ description ?? defaultDescription }}
      </p>
    </div>
  </div>
</template>
<style scoped>
.unauth-wrapper {
  min-height: 56vh; /* centraliza bem sem ficar enorme */
}

/* SVG “clean” */
.unauth-svg .ring {
  fill: none;
  stroke: #dcdfe4; /* cinza claro (funciona em light e dark) */
  stroke-width: 20;
}
.unauth-svg .handle {
  fill: #dcdfe4;
}
.unauth-svg .dot-bg {
  fill: #eeeeee;
}
.unauth-svg .question {
  font:
    700 72px/1.1 ui-sans-serif,
    -apple-system,
    'Segoe UI',
    Roboto,
    'Helvetica Neue',
    Arial;
  fill: #6b7280; /* cinza médio */
}

/* Dark mode suave (Vuetify alterna data-theme=dark no html) */
:global(html[data-theme='dark']) .unauth-svg .ring,
:global(html[class*='v-theme--dark']) .unauth-svg .ring {
  stroke: #3e444c;
}
:global(html[data-theme='dark']) .unauth-svg .handle,
:global(html[class*='v-theme--dark']) .unauth-svg .handle {
  fill: #3e444c;
}
:global(html[data-theme='dark']) .unauth-svg .dot-bg,
:global(html[class*='v-theme--dark']) .unauth-svg .dot-bg {
  fill: #2b3036;
}
:global(html[data-theme='dark']) .unauth-svg .question,
:global(html[class*='v-theme--dark']) .unauth-svg .question {
  fill: #c7cad1;
}

/* gap helper (Vuetify ainda não tem util gap-x) */
.gap-2 {
  gap: 0.5rem;
}
</style>
